import React, { useState } from 'react';
import {
  FileText,
  ShieldCheck,
  CheckCircle2,
  Save,
  ExternalLink,
  Info,
} from 'lucide-react';
import { AuthUser, PlanType } from '../types';

interface SettingsTabProps {
  domain: string;
  authUser: AuthUser | null;
  currentPlan: PlanType;
  onOpenSecurityModal: () => void;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
  domain,
  authUser,
  currentPlan,
  onOpenSecurityModal,
}) => {
  const [defaultDisclaimer, setDefaultDisclaimer] = useState(
    'Treść tej wiadomości zawiera informacje poufne przeznaczone wyłącznie dla adresata. Jeśli otrzymałeś tę wiadomość przez pomyłkę, prosimy o jej usunięcie oraz powiadomienie nadawcy.'
  );

  const [savedSuccess, setSavedSuccess] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSavedSuccess(true);
    setTimeout(() => setSavedSuccess(false), 3000);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 960, margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0B192C', margin: '0 0 6px' }}>
          Ustawienia Organizacji i Integracji
        </h2>
        <p style={{ fontSize: 13, color: '#475569', margin: 0 }}>
          Zarządzaj globalną notką RODO oraz weryfikuj uprawnienia API dla domeny{' '}
          <strong style={{ color: '#0066FF' }}>{domain}</strong>.
        </p>
      </div>

      {/* Info notice about company registration data */}
      <div
        style={{
          background: 'rgba(0, 102, 255, 0.05)',
          border: '1px solid rgba(0, 102, 255, 0.2)',
          borderRadius: 10,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Info size={18} color="#0066FF" style={{ flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, color: '#334155', lineHeight: 1.5 }}>
          Dane rejestrowe i kontaktowe firmy (nazwa, logo, NIP, adres i WWW) są synchronizowane bezpośrednio w <strong>Kreatorze Stopki</strong> w zakładkach <strong>Firma & Logo</strong> oraz <strong>Kontakt</strong>.
        </span>
      </div>

      <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Global Disclaimer */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            boxShadow: '0 1px 3px rgba(11, 25, 44, 0.05)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #e2e8f0', paddingBottom: 14 }}>
            <FileText size={20} color="#059669" />
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C', margin: 0 }}>
              Domyślna notka prawna i klauzula RODO
            </h3>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#0B192C', marginBottom: 6 }}>
              Treść klauzuli dołączanej do stopek
            </label>
            <textarea
              rows={3}
              value={defaultDisclaimer}
              onChange={(e) => setDefaultDisclaimer(e.target.value)}
              style={{
                width: '100%',
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: 8,
                padding: '10px 14px',
                color: '#0B192C',
                fontSize: 13,
                resize: 'vertical',
                lineHeight: 1.5,
              }}
            />
            <p style={{ fontSize: 11.5, color: '#64748b', marginTop: 6, marginBottom: 0 }}>
              Ten tekst pojawi się pod podpisem każdego pracownika, jeśli w szablonie włączona jest opcja „Notka prawna / RODO”.
            </p>
          </div>
        </div>

        {/* Security & API Status */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 12,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            boxShadow: '0 1px 3px rgba(11, 25, 44, 0.05)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: 14, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ShieldCheck size={20} color="#0066FF" />
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C', margin: 0 }}>
                Status uprawnień Google Workspace API
              </h3>
            </div>
            <button
              type="button"
              onClick={onOpenSecurityModal}
              className="btn btn-outline"
              style={{
                padding: '6px 12px',
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                borderColor: '#cbd5e1',
                color: '#0066FF',
                background: '#ffffff',
              }}
            >
              <span>Szczegóły uprawnień</span>
              <ExternalLink size={13} />
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: 8 }}>
              <span style={{ color: '#475569' }}>Aktywny administrator:</span>
              <strong style={{ color: '#0B192C' }}>{authUser?.email || 'Niezalogowany'}</strong>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: 8 }}>
              <span style={{ color: '#475569' }}>Aktywny pakiet subskrypcji:</span>
              <strong style={{ color: '#0066FF', textTransform: 'uppercase' }}>{currentPlan}</strong>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: 8 }}>
              <span style={{ color: '#475569' }}>Uprawnienia integracji Google:</span>
              <span style={{ color: '#0066FF', fontSize: 12, fontWeight: 600, background: 'rgba(0, 102, 255, 0.08)', padding: '2px 8px', borderRadius: 4 }}>
                Tylko edycja pola podpisu
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, background: '#f8fafc', border: '1px solid #e2e8f0', padding: '10px 14px', borderRadius: 8 }}>
              <span style={{ color: '#475569' }}>Ochrona prywatności:</span>
              <span style={{ color: '#059669', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                <CheckCircle2 size={16} /> 100% Prywatności (Brak wglądu w maile)
              </span>
            </div>
          </div>
        </div>

        {/* Save button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <button
            type="submit"
            className="btn btn-primary"
            style={{
              background: 'linear-gradient(135deg, #0066FF 0%, #0052cc 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 22px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              boxShadow: '0 2px 8px rgba(0, 102, 255, 0.25)',
            }}
          >
            <Save size={16} />
            <span>Zapisz ustawienia</span>
          </button>

          {savedSuccess && (
            <span style={{ color: '#059669', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
              <CheckCircle2 size={16} /> Ustawienia zostały zapisane!
            </span>
          )}
        </div>
      </form>
    </div>
  );
};
